import { WhereItIs, fullLabel } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { PhotoFixAction, PhotoMoveTrail, RemovedPhotosStrip } from "@/components/PhotoFixAction";
import { MainPhotoCell } from "@/components/MainPhotoCell";

import { FloorActionButton } from "@/floor/FloorActionButton";
import { FloorTruckConditionCard } from "@/floor/FloorTruckConditionCard";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { FloorPlaceSector } from "@/floor/FloorPlaceSector";
import { FloorPhotoChoice } from "@/floor/FloorPhotoChoice";
import { usePhotoSources } from "@/components/PhotoSourceButtons";
import { useFlash } from "@/floor/useFlash";
import { useFloorActions } from "@/lib/floor";
import { useGoDesk } from "@/lib/floorReturn";
import { useDonorStatuses } from "@/lib/vehicleLists";
import { TruckRoleTag } from "@/components/TruckRoleTag";
import { ArrowLeft, Truck, Loader2, ExternalLink, MapPin, Undo2, CheckCircle2, X } from "lucide-react";

const STATUS_BG = {
  slate: "bg-slate-500 text-white", blue: "bg-blue-600 text-white", sky: "bg-sky-500 text-white",
  emerald: "bg-emerald-600 text-white", amber: "bg-amber-500 text-slate-900",
  orange: "bg-[#E0781A] text-white", red: "bg-[#B01E28] text-white",
  indigo: "bg-indigo-600 text-white", purple: "bg-purple-600 text-white",
};

export default function FloorTruckCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const [donor, setDonor] = useState(null);
  const [overlay, setOverlay] = useState(null); // 'add' | 'place' | 'condition' | 'photo'
  const [added, setAdded] = useState(null);     // {saved, dup, failed} — stays on the card after a photo save
  const [busy, setBusy] = useState(false);
  // THE LAST MOVE STAYS ON THE SCREEN WITH ITS UNDO until he leaves it — the existing batch undo,
  // which REFUSES to put back a truck somebody else has moved since and names it instead.
  const [placed, setPlaced] = useState(null);   // {batch_id, label}
  const { trigger, FlashOverlay } = useFlash();
  const actions = useFloorActions("truck");
  const statuses = useDonorStatuses();

  const load = async () => {
    try {
      const { data } = await api.get(`/donors/${id}`);
      setDonor(data);
    } catch { setDonor(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doAddPart = async (tagCode) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${id}/capture`, { tag_code: tagCode });
      trigger("success", `PART ADDED · ${data.part?.short_code || ""}`); await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "ADD FAILED"); }
    finally { setBusy(false); }
  };

  const savePhotos = async (files) => {
    setBusy(true);
    setAdded(null);
    let saved = 0, dup = 0;
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post(`/donors/${id}/photos`, fd, { timeout: 120000 });
        if (data.duplicate) dup += 1; else saved += 1;
      }
      const words = saved === 0 ? "ALREADY HAVE IT" : `${saved} PHOTO${saved === 1 ? "" : "S"} ADDED`;
      trigger("success", words); setAdded({ saved, dup }); await load();
    } catch (e2) {
      // the ones that made it are still said — the tech knows exactly what to re-shoot
      if (saved) setAdded({ saved, dup, failed: true });
      trigger("error", apiErrorMessage(e2) || "PHOTO FAILED");
    }
    finally { setBusy(false); }
  };
  const photo = usePhotoSources({ onFiles: savePhotos, testidPrefix: "floor-truck-photo" });

  const doPlaced = async (data) => {
    trigger("success", `${donor?.stock_number || "TRUCK"} → ${(data.destination?.name || data.destination?.short_code || "").toUpperCase()}`);
    setPlaced({ batch_id: data.batch_id,
                label: fullLabel(data.destination) || data.destination?.name || data.destination?.short_code });
    setOverlay(null);
    await load();
  };

  const undoPlace = async () => {
    if (!placed?.batch_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/place-batch/${placed.batch_id}/undo`, {});
      if (!data.ok) { trigger("error", data.message || "COULD NOT PUT IT BACK"); }
      else { trigger("success", "PUT BACK"); setPlaced(null); }
      await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "UNDO FAILED"); }
    finally { setBusy(false); }
  };

  const HANDLERS = {
    truck_add_part: () => setOverlay("add"),
    truck_photo: () => setOverlay("photo"),
    truck_rescan: () => navigate("/floor"),
    truck_move: () => setOverlay("place"),
    truck_condition: () => setOverlay("condition"),
  };

  if (donor === false) return <NotFound onBack={() => navigate("/floor")} />;
  if (!donor) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;

  // Rating rides along with being at the truck — it is not a separate errand on another screen.
  if (overlay === "condition") {
    return <FloorTruckConditionCard donorId={id} stock={donor.stock_number} onBack={() => setOverlay(null)} />;
  }

  // The photo on this card is the truck's MAIN photo (the same one the Vehicles list shows), so
  // "Wrong photo?" acts on the photo the tech is actually looking at.
  const livePhotos = (donor.photos || []).filter((p) => !p.is_archived && p.file_id);
  const firstPhoto = livePhotos.find((p) => p.file_id === donor.main_photo?.file_id) || livePhotos[0];
  const st = statuses.find((s) => s.code === donor.status);
  const ymm = [donor.year, donor.make, donor.model].filter(Boolean).join(" ") || "Vehicle";
  const shortVin = donor.vin ? `…${String(donor.vin).slice(-6)}` : null;

  return (
    <div className="flex-1 flex flex-col" data-testid="floor-truck-card">
      {FlashOverlay}
      {photo.inputs}
      <FloorPhotoChoice open={overlay === "photo"} testid="floor-truck-photo-choice"
        onCamera={photo.openCamera} onGallery={photo.openGallery} onClose={() => setOverlay(null)} />

      <div className="p-4 sm:p-6 flex flex-col gap-5 flex-1">
        <button data-testid="floor-back" onClick={() => navigate("/floor")}
          className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start">
          <ArrowLeft className="w-5 h-5" /> Scan
        </button>

        <div className="flex gap-4 items-center">
          <div className="shrink-0 flex flex-col items-center gap-1.5">
            {/* THE PHOTO A TECHNICIAN SEES IS THE PHOTO THE YARD SEES. It used to be whichever
                photo happened to be first in the array; it is now the truck's MAIN photo, the same
                one the Vehicles list shows — and the count opens every photo, so the person who
                SHOT them can pick the one that shows the truck. He holds `archive_photos`, which
                is what gates that choice, and this is the only screen he can land on. */}
            {firstPhoto ? (
              <MainPhotoCell recordId={id} stockNumber={donor.stock_number} main={donor.main_photo}
                photoCount={donor.photo_count || 0} onChanged={load}
                size="w-28 h-28" big testidPrefix="floor-truck-main-photo" />
            ) : (
              <div className="w-28 h-28 rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-100">
                <div className="w-full h-full flex items-center justify-center"><Truck className="w-10 h-10 text-slate-300" /></div>
              </div>
            )}
            {firstPhoto && (
              <PhotoFixAction scope="donor" recordId={id} photo={firstPhoto} onDone={load} big
                testid="floor-truck-photo-fix" label="Wrong photo?" />
            )}
          </div>
          <div className="min-w-0">
            <div data-testid="floor-truck-ymm" className="font-display text-3xl font-black tracking-tight leading-tight truncate">{ymm}</div>
            <div className="font-mono text-lg text-slate-500 mt-0.5">{donor.stock_number}{shortVin ? ` · VIN ${shortVin}` : ""}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span data-testid="floor-truck-status" className={`rounded-lg px-3 py-1 text-base font-bold uppercase tracking-wide ${STATUS_BG[st?.color] || "bg-slate-500 text-white"}`}>
                {st?.name || donor.status || "—"}
              </span>
              <TruckRoleTag role={donor.role} size="md" testid="floor-truck-role" />
            </div>
          </div>
        </div>

        <PhotoMoveTrail scope="donor" recordId={id} onDone={load} testid="floor-truck-moves" />

        {added && added.saved === 0 && added.dup > 0 && (
          <div data-testid="floor-truck-photos-dup" data-count={added.dup}
            className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3">
            <div className="font-display text-xl font-black text-amber-900 leading-tight">
              ALREADY ON THIS TRUCK — {added.dup} PHOTO{added.dup === 1 ? "" : "S"} SKIPPED
            </div>
            <button type="button" data-testid="floor-truck-photos-dup-close" aria-label="Dismiss" onClick={() => setAdded(null)}
              className="ml-auto p-1 text-amber-800"><X className="w-6 h-6" /></button>
          </div>
        )}
        {added && added.saved > 0 && (
          <div data-testid="floor-truck-photos-added" data-count={added.saved}
            className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-2xl font-black text-emerald-800 leading-tight">
                {added.saved} PHOTO{added.saved === 1 ? "" : "S"} ADDED
              </div>
              <div className="text-sm font-semibold text-emerald-900/80">
                {added.dup ? `${added.dup} already on this truck, skipped. ` : ""}
                {added.failed ? "The rest did not save — take them again." : `Now ${livePhotos.length} on this truck.`}
              </div>
            </div>
            <button type="button" data-testid="floor-truck-photos-added-close" aria-label="Dismiss" onClick={() => setAdded(null)}
              className="ml-auto p-1 text-emerald-700"><X className="w-6 h-6" /></button>
          </div>
        )}

        <RemovedPhotosStrip scope="donor" recordId={id} photos={donor.photos} onDone={load}
          testid="floor-truck-removed" />

        {/* WHERE IT IS NOW — he should not have to press anything to find out, and an unplaced
            truck says NOT PLACED in amber rather than showing a blank or a dash. */}
        <div data-testid="floor-truck-where" className="rounded-xl border-2 border-[#E2E8F0] bg-white px-4 py-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">Where it is now</div>
          {donor.location_label
            ? <div data-testid="floor-truck-where-label" className="text-lg font-bold text-slate-800 leading-snug">
                <WhereItIs location={donor.location || { full_label: donor.location_label, short_code: donor.location_code }}
                  size="lg" icon={false} testid="floor-truck-where-full" />
                {donor.location_holding && <span className="ml-2 text-sm font-bold text-[#C76714]">holding area</span>}
              </div>
            : <div data-testid="floor-truck-where-unplaced" className="text-lg font-black text-[#C76714]">NOT PLACED</div>}
        </div>

        {/* THE MOVE JUST MADE, WITH ITS UNDO. Stays until he leaves the screen. */}
        {placed && (
          <div data-testid="floor-place-done" className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 space-y-2.5">
            <div className="text-lg font-bold text-emerald-800 leading-snug">
              ✓ {donor.stock_number} → {placed.label}
            </div>
            <button type="button" data-testid="floor-place-undo" onClick={undoPlace} disabled={busy}
              className="w-full h-16 rounded-xl border-2 border-emerald-400 bg-white text-lg font-bold text-emerald-800 flex items-center justify-center gap-2.5 active:scale-95 active:bg-emerald-100 transition-[transform,background-color] duration-100 disabled:opacity-60">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Undo2 className="w-6 h-6" strokeWidth={2.2} />}
              UNDO THIS MOVE
            </button>
          </div>
        )}

        {/* PLACE IN SECTOR — the card's ONE way to move a truck. It replaces the old Move action
            rather than sitting beside it, so there is never a second path to the same job, and it
            only appears while that action is switched on in Floor Buttons. */}
        {actions.some((a) => a.code === "truck_move") && (
          <button type="button" data-testid="floor-truck-place" onClick={() => setOverlay("place")}
            disabled={busy}
            className="w-full h-20 rounded-xl bg-[#B01E28] text-white text-2xl font-black flex items-center justify-center gap-3 active:scale-95 active:bg-[#8B1820] transition-[transform,background-color] duration-100 disabled:opacity-60">
            <MapPin className="w-8 h-8" strokeWidth={2.2} /> PLACE IN SECTOR
          </button>
        )}

        <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="floor-truck-actions">
          {actions.filter((a) => a.code !== "truck_move").map((a) => (
            <FloorActionButton key={a.code} testid={`floor-action-${a.code}`} label={a.label}
              icon={a.icon} tone={a.tone} busy={busy && overlay === null} onClick={HANDLERS[a.code] || (() => {})} />
          ))}
        </div>

        {/* Escape hatch — full desk record (edit, specs, AI workbench); returns to Floor */}
        <button data-testid="floor-truck-full-detail" onClick={() => goDesk(`/donors/${id}`)}
          className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-700 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform duration-100">
          <ExternalLink className="w-5 h-5 text-[#B01E28]" /> See Full Detail
        </button>
      </div>

      <FloorScanOverlay open={overlay === "add"} testid="floor-addpart-overlay"
        title="Scan a fresh FAS tag" sub={`Log a part off ${donor.stock_number}`} submitLabel="Add part" continuous
        onScan={doAddPart} onClose={() => setOverlay(null)} busy={busy} />

      {/* PLACE IN SECTOR — big sector buttons instead of a scan overlay, as approved. */}
      {overlay === "place" && (
        <FloorPlaceSector donorId={id} stock={donor.stock_number}
          current={{ id: donor.site_location_id, label: donor.location_label }}
          onClose={() => setOverlay(null)} onPlaced={doPlaced} />
      )}
    </div>
  );
}

function NotFound({ onBack }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6" data-testid="floor-card-notfound">
      <div className="text-2xl font-black text-slate-700">This truck wasn’t found.</div>
      <FloorActionButton label="Back to scan" icon="ScanLine" tone="primary" onClick={onBack} testid="floor-notfound-back" />
    </div>
  );
}
